<template>
    <div class="rounded-lg border-2 p-3 text-sm" :class="border">
        <div class="flex items-center gap-2 font-medium">
            <UIcon name="i-lucide-clipboard-check" class="size-4 shrink-0" />
            {{ operation.summary }}
        </div>

        <ul class="mt-2 list-disc ms-5 text-muted">
            <li v-for="(line, i) in operation.preview" :key="i"><AssistantText :text="line" /></li>
        </ul>

        <div v-if="decidable" class="mt-3 flex gap-2">
            <UButton
                :label="$t('assistant.confirm')"
                icon="i-lucide-check"
                size="sm"
                :loading="deciding"
                @click="emit('decide', true)"
            />
            <UButton
                :label="$t('assistant.decline')"
                icon="i-lucide-x"
                size="sm"
                color="neutral"
                variant="subtle"
                :disabled="deciding"
                @click="emit('decide', false)"
            />
        </div>

        <!-- The line that says what happened, and the one place the guest link an
             offer was just issued under is ever shown. It is followable and copyable
             (AssistantText.vue) rather than forty characters to select by hand. -->
        <p v-else-if="operation.status !== 'proposed'" class="mt-2 flex items-baseline gap-1 text-xs" :class="tone">
            <UIcon :name="icon" class="size-4 shrink-0 self-center" />
            <AssistantText :text="label" />
        </p>
    </div>
</template>

<script setup lang="ts">
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'

    import AssistantText from '@/components/AssistantText.vue'
    import { chatOperationResult } from '@/libs/chatResult'
    import type { IChatOperation } from '@/types/Chat'

    /**
     * One proposal: the preview the server built, and the two buttons that decide it.
     *
     * Lifted out of Assistant.vue so that a step of a plan and a lone proposal are drawn
     * by the same code. They were the same thing before plans existed and staying that
     * way is the point — the desk should not be able to tell which of the two it is
     * looking at.
     */

    const props = defineProps<{
        operation: IChatOperation
        /** Whether this card may be pressed at all: the reader's own thread, and — inside
         *  a plan — the step whose turn it is. */
        actionable: boolean
        deciding:   boolean
    }>()

    const emit = defineEmits<{ decide: [confirm: boolean] }>()

    const { t } = useI18n()

    const decidable = computed((): boolean => props.actionable && props.operation.status === 'proposed')

    const border = computed((): string => {
        if (props.operation.status === 'proposed') return 'border-primary/60'
        if (props.operation.status === 'executed') return 'border-success/60'

        return 'border-accented'
    })

    const tone = computed((): string => {
        if (props.operation.status === 'executed') return 'text-success'

        return props.operation.status === 'failed' ? 'text-error' : 'text-muted'
    })

    const icon = computed((): string => {
        if (props.operation.status === 'executed') return 'i-lucide-circle-check'

        return props.operation.status === 'failed' ? 'i-lucide-circle-x' : 'i-lucide-ban'
    })

    // What the execution did comes from its structured outcome, translated here — never
    // from the sentence the service wrote for its own audit trail (libs/chatResult.ts).
    const label = computed((): string => {
        const result = chatOperationResult(props.operation)

        if (props.operation.status === 'executed') return t('assistant.executed') + (result ? ` — ${result}` : '')
        if (props.operation.status === 'failed') return t('assistant.op_failed') + (result ? ` — ${result}` : '')

        return t('assistant.declined')
    })
</script>
